import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import Modal from 'react-modal';
import { ReactComponent as IconSecurity } from '../../../assets/icons/iconSecurity.svg';
import { sendCodeEmail } from '../../../controller/EmailController';
import { Button } from '../../Buttons/Button';
import { ModalInputCode } from '../ModalInputCode';
import { Container } from './styles';
import usePersistedState from '../../../hooks/usePersistedState';
import { decryptCode } from '../../../utils/crypto';
import { useAlert } from '../../../hooks/useAlert';
import { useApp } from '../../../context';
import { unMask } from '../../../utils/removeMask';
import { UserController } from '../../../controller/UserController';
import { useConfig } from '../../../context/configCustom';

interface IData {
  contactValue: string;
  contactLabel: string;
  codeExpected: string;
}

interface SendVerificationModalProps {
  isOpen: boolean;
  data: IData;
  isRequesting?: boolean;
  onCodeChange: (code: string) => void;
  onRequestClose: () => void;
  onCallback: () => void;
}

export function SendVerificationModal({
  isOpen,
  data,
  onCodeChange,
  onRequestClose,
  onCallback,
  isRequesting,
}: SendVerificationModalProps) {
  const TIMER = 60000;
  const { pathResourceEmail } = useConfig();
  const app = useApp();
  const alert = useAlert();
  const [secondsRemainingToResend, setSecondsRemainingToResend] = useState(TIMER / 1000);
  const [dateToSend, setDateToSend] = usePersistedState<number | undefined>(
    'dateToSend',
    undefined,
  );
  const [defaultValues] = useState({ inputCode: '' });
  const {
    formState: { errors },
    control,
    clearErrors,
    setError,
    setValue,
  } = useForm({ defaultValues });

  const code = useWatch({
    control,
    name: 'inputCode',
  });

  const [disableResend, setDisableResend] = useState(
    dateToSend === undefined ? false : dateToSend + TIMER > new Date().getTime(),
  );
  const handleResendCode = () => {
    if (!pathResourceEmail) {
      alert.show({
        type: 'error',
        message: 'Não foi possível enviar o código de verificação, tente novamente!',
      });
      return;
    }
    setDateToSend(new Date().getTime());
    app.setLoading(true);
    if (data.contactLabel === 'WhatsApp') {
      let unmaskedNumber = unMask(data.contactValue);
      if (unmaskedNumber[2] === '9') {
        unmaskedNumber = unmaskedNumber.slice(0, 2) + unmaskedNumber.slice(3);
      }

      UserController.sendWhatsappConfirmation(`55${unmaskedNumber}`)
        .then((resCode) => {
          onCodeChange(decryptCode(resCode));
        })
        .catch(() => {
          alert.show({
            message: 'Não foi possível enviar o código de verificação, tente novamente!',
            type: 'error',
          });
        });
    } else {
      sendCodeEmail({
        type: 'activeAccount',
        to: data.contactValue,
        pathResource: pathResourceEmail,
      })
        .then((resCode) => {
          onCodeChange(decryptCode(resCode));
        })
        .catch(() => {
          alert.show({
            type: 'error',
            message: 'Não foi possível enviar o e-mail, tente novamente!',
          });
        })
        .finally(() => app.setLoading(false));
    }
  };

  const handleOnCallback = () => {
    if (!code) {
      setError('inputCode', { message: 'Campo obrigatório' });
      return;
    }
    if (data.codeExpected === code) {
      onCallback();
      setValue('inputCode', '');
    } else {
      setError('inputCode', {
        message: 'Código não corresponde',
      });
    }
  };

  const handleCloseModal = () => {
    if (isRequesting) return;
    setValue('inputCode', '');
    onRequestClose();
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (dateToSend !== undefined) {
      interval = setInterval(() => {
        const isDisabled = dateToSend + TIMER > new Date().getTime();
        setDisableResend(isDisabled);
        setSecondsRemainingToResend((prev) => prev - 1);
        if (!isDisabled) {
          setSecondsRemainingToResend(TIMER / 1000);
          setDateToSend(undefined);
          clearInterval(interval);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [dateToSend, setDateToSend]);

  useEffect(() => {
    if (code) clearErrors('inputCode');
  }, [clearErrors, code]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleCloseModal}
      overlayClassName="react-modal-overlay"
      className="react-modal-content">
      <Container>
        <div className="divTitle">
          <IconSecurity />
          <div>
            <p>Enviamos para o {data.contactLabel} </p>
            <span className="spanEmail">{data.contactValue}</span>
            <p>um código de verificação.</p>
          </div>
        </div>
        <ModalInputCode
          name="inputCode"
          control={control}
          placeholder="Insira aqui o código"
          fullWidth
          width="100%"
          errorText={errors.inputCode?.message}
          error={!!errors.inputCode}
          onCallSubmit={handleOnCallback}
        />
        <div className="divVerify">
          <Button
            text={
              disableResend ? `Reenviar código (${secondsRemainingToResend})` : 'Reenviar código'
            }
            dimensions={{ width: '8rem', height: '1.875rem' }}
            disabled={disableResend || isRequesting}
            background="transparent"
            color="primary"
            textHoverColor="secondary"
            style={{ textDecoration: 'underline' }}
            font={{ size: '1.5rem', weight: 500 }}
            onClick={handleResendCode}
          />
          <Button
            onClick={handleOnCallback}
            text="Verificar"
            disabled={isRequesting}
            dimensions={{ width: '12rem', height: '1.875rem' }}
          />
        </div>
      </Container>
    </Modal>
  );
}
